import {
  Batch,
  getUserDataByUsername,
  getUserFollowersRef,
  getUserRef,
  getUsersRef,
  getUserSubscriptionsSnap,
} from "./FirebaseUtils";
import {
  AccountData,
  Membership,
  MembershipPlanFrequency,
  MembershipState,
  OVERFLOW_RATE,
  OVERFLOW_RATE_PRO,
  OVERFLOW_RATE_BUSINESS,
  PlanTypeV4,
  Subscription,
} from "@markit/common.types";
import {
  arrayUnion,
  getCountFromServer,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
} from "../firebase";
import {
  hasSubscriptionUnlockedAdvancedData,
  subscriptionPlanType,
} from "@markit/common.utils";

/**
 * The uid of the user who referred this user, or undefined if they haven't
 * been referred. A user can only ever be referred once.
 */
export const getReferrerUid = async (
  refereeUserId: string
): Promise<string | undefined> => {
  const referrersSnap = await getDocs(
    query(
      getUsersRef(),
      where("profileReferrals", "array-contains", refereeUserId),
      limit(1)
    )
  );
  return referrersSnap.docs[0]?.id;
};

/**
 * Gets called when a user signs up or purchases a subscription with a referral code
 * @param referrerUserId uid of the referrer
 * @param refereeUserId uid of the referee
 */
export const createSubscriptionReferral = async (
  referrerUserData: AccountData,
  refereeUserId: string
) => {
  // A user can only be referred once, by the first referral code they redeem
  if (
    referrerUserData.profileReferrals.includes(refereeUserId) ||
    (await getReferrerUid(refereeUserId)) !== undefined
  ) {
    return;
  }
  const batch = new Batch("Error adding to profileReferrals");
  const referrerUserRef = getUserRef(referrerUserData.uid);

  batch.update(referrerUserRef, {
    profileReferralsLastTimestamp: new Date().toISOString(),
    profileReferrals: arrayUnion(refereeUserId),
  });

  await batch.commit();
};

// Referral code from a shared link (markitai.com/welcome?referral=CODE), kept in
// localStorage so it survives sign up/onboarding and gets pre-filled at checkout
const REFERRAL_CODE_STORAGE_KEY = "markitReferralCode";
const REFERRAL_CODE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Saves the ?referral= query param if the page was opened with one. Called once
 * on page load, before any redirect drops the query string.
 */
export const saveReferralCodeFromUrl = () => {
  try {
    const code = new URLSearchParams(window.location.search)
      .get("referral")
      ?.trim()
      .toUpperCase();
    if (code) {
      localStorage.setItem(
        REFERRAL_CODE_STORAGE_KEY,
        JSON.stringify({ code, savedAt: Date.now() })
      );
    }
  } catch {
    // ignore storage errors
  }
};

/** The saved referral code, or "" if there isn't one or it's expired */
export const getSavedReferralCode = (): string => {
  try {
    const saved = localStorage.getItem(REFERRAL_CODE_STORAGE_KEY);
    if (!saved) {
      return "";
    }
    const { code, savedAt } = JSON.parse(saved);
    if (
      typeof code !== "string" ||
      typeof savedAt !== "number" ||
      Date.now() - savedAt > REFERRAL_CODE_TTL_MS
    ) {
      localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
      return "";
    }
    return code;
  } catch {
    return "";
  }
};

export const clearSavedReferralCode = () => {
  try {
    localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
};

// Checks if a code is valid and if the user has a Pro or Business subscription
export const checkValidUserReferralCode = async (
  code: string
): Promise<{
  codeError: string;
  userData: AccountData | undefined;
}> => {
  const userData = await getUserDataByUsername(code.toLowerCase());
  if (userData) {
    // Check if referrer has creator or business subscription
    if (!hasSubscriptionUnlockedAdvancedData(userData)) {
      return {
        codeError:
          "This referral code isn't active. Only Markit+ Pro or Business accounts can refer you.",
        userData,
      };
    }
    return {
      codeError: "",
      userData: userData,
    };
  }
  return {
    codeError: "",
    userData: undefined,
  };
};

/**
 * Records the referral from a saved referral link when a creator finishes
 * onboarding, so they count as referred even on the free plan and the
 * referrer gets credit if they upgrade later. The saved code is kept so
 * it's still pre-filled at checkout.
 */
export const createReferralFromSavedCode = async (referee: AccountData) => {
  const savedCode = getSavedReferralCode();
  if (
    savedCode === "" ||
    savedCode.toLowerCase() === referee.username.toLowerCase()
  ) {
    return;
  }
  const { codeError, userData } = await checkValidUserReferralCode(savedCode);
  if (userData && codeError === "") {
    await createSubscriptionReferral(userData, referee.uid);
  }
};

// Gets the number of non-free membership subscriptions for a creator
export const getNumPaidMembershipSubscriptions = async (userId: string) => {
  const followersRef = getUserFollowersRef(userId);
  const query_ = query(followersRef, where("membershipPlanId", "!=", "free"));
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

// Gets the number of paid members for a specific membership plan
export const getNumMembershipSubscriptionMembers = async (
  userId: string,
  membershipId: string
) => {
  const followersRef = getUserFollowersRef(userId);
  const query_ = query(
    followersRef,
    where("membershipPlanId", "==", membershipId)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

// Just change the membership state to inactive when a user disables memberships
export const disableMemberships = async (
  userId: string,
  membership: Membership
) => {
  const userRef = getUserRef(userId);
  const updatedMembership: Membership = {
    ...membership,
    state: MembershipState.INACTIVE,
  };
  updateDoc(userRef, { membership: updatedMembership as any });
};

// Returns the shortened label for display of the membership frequency
export const getShortMembershipFrequencyLabel = (
  frequency: MembershipPlanFrequency
) => {
  if (
    frequency === MembershipPlanFrequency.MONTHLY ||
    frequency === MembershipPlanFrequency.FOREVER
  ) {
    return "mo";
  } else if (frequency === MembershipPlanFrequency.ANNUALLY) {
    return "yr";
  }
};

// Gets the user's subscription history (subscription documents + current subscription)
export const getAllUserSubscriptions = async (
  accountData: AccountData
): Promise<Subscription[]> => {
  const subscriptionsSnap = await getUserSubscriptionsSnap(accountData.uid);
  const fromDocs = subscriptionsSnap.docs.map((doc) => doc.data());
  const current = accountData.customer.currentSubscription;

  const byStart = new Map<string, Subscription>();
  for (const sub of fromDocs) {
    byStart.set(sub.start, sub);
  }
  if (current && current.start) {
    byStart.set(current.start, current);
  }

  return Array.from(byStart.values()).sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );
};

// Maps Stripe overflow price IDs to their per-credit rate in cents.
// Required to correctly display historical billing when the overflow rate has changed
// (e.g., Business plan changed from 1.0¢ to 1.1¢ on 5/24/2026).
// Move to Bit at some point.
const OVERFLOW_PRICE_RATE_MAP: Record<string, number> = {
  // V4 prod
  price_1PjCKTHyH26KfMnYvTgofGKY: OVERFLOW_RATE, // Essential: 1.5¢
  price_1R1v3NHyH26KfMnY4kD0FK8g: OVERFLOW_RATE_PRO, // Pro: 1.25¢
  price_1Tap9SHyH26KfMnYsuxwxKZK: OVERFLOW_RATE_BUSINESS, // Business: 1.1¢ (current)
  price_1PkK4FHyH26KfMnYgw47KxoW: 1.0, // Business: 1.0¢ (deprecated 5/24/2026)
  // V4 test
  price_1PjCNCHyH26KfMnY99wZAjY0: OVERFLOW_RATE,
  price_1R1v26HyH26KfMnYAR1pItzq: OVERFLOW_RATE_PRO,
  price_1TapO4HyH26KfMnYPP0OAFml: OVERFLOW_RATE_BUSINESS,
  price_1PjFYKHyH26KfMnY7xghNPz4: 1.0, // Business test: deprecated 5/24/2026
  // V3 prod & test (same rate for all plans)
  price_1NB3eOHyH26KfMnYuevu7ft4: OVERFLOW_RATE,
  price_1NAz8XHyH26KfMnYwqy7AjBu: OVERFLOW_RATE,
};

/**
 * Returns the overflow rate in cents for a historical subscription cycle.
 * Reads from the overflow price ID stored on the subscription's price array so
 * that past billing entries reflect the rate that was actually charged, not the
 * user's current plan rate. Falls back to the plan-derived rate if no overflow
 * price item is found (e.g. cycle ended before any overflow occurred).
 * Move to Bit at some point.
 */
export const overflowRateForSubscription = (
  sub: Subscription,
  userId: string,
  isTest: boolean
): number => {
  const overflowPriceItem = sub.price.find((p) => p.type === "overflow");
  if (
    overflowPriceItem !== undefined &&
    OVERFLOW_PRICE_RATE_MAP[overflowPriceItem.id] !== undefined
  ) {
    return OVERFLOW_PRICE_RATE_MAP[overflowPriceItem.id];
  }
  const plan = subscriptionPlanType(sub.price[0]?.id, userId, isTest);
  if (plan === PlanTypeV4.PRO) return OVERFLOW_RATE_PRO;
  if (plan === PlanTypeV4.BUSINESS) return OVERFLOW_RATE_BUSINESS;
  return OVERFLOW_RATE;
};

// Accumulates the total message count for all subscriptions for a user + the current subscription message count
export const getUserTotalMessagesAllTime = async (userData: AccountData) => {
  const subscriptionsSnap = await getUserSubscriptionsSnap(userData.uid);
  const totalPastMessages = subscriptionsSnap.docs.reduce((total, doc) => {
    return total + doc.data().message;
  }, 0);
  const currentMessages = userData.customer.currentSubscription?.message || 0;
  return totalPastMessages + currentMessages;
};
